import { PublicClientApplication } from '@azure/msal-browser';
import axios from 'axios';
import decodeJWT from 'jwt-decode';
import { configurationService } from 'subjects/crossModules/ConfigurationService';

// Do not call functions that change the user's authenticated state (login, logout) outside the react context provided by MsalProvider as the components inside the context may not properly update.
// If you want to login/logout or execute any function that modifies user's authenticated state use `src/hooks/useAuth`
class MSALUtil {
    ACCESS_TOKEN = 'access_token'; // do not change this value; it is used as such in different places
    ACCESS_TOKEN_FETCH_PARAMS = 'access_token_fetch_params';
    ACCESS_TOKEN_API_LAST_ACCESSED = 'access_token_api_last_accessed';
    USER_DEFAULT_MAX_INACTIVE_SECONDS = 3600; // 1 hour
    USER_NEEDS_TO_LOGIN_AGAIN = 'user_needs_to_login_again';
    USER_MAX_INACTIVE_SECONDS = 'user_max_inactive_seconds';
    OPENID_CONFIGURATION = "openid_configuration";

    isTokenExpired() {
        const token = sessionStorage.getItem(this.ACCESS_TOKEN);
        if (!token) {
            return true;
        }

        const decoded = decodeJWT(token);

        if (!decoded.exp) {
            return true;
        }

        return Date.now() > decoded.exp * 1000;
    }

    initSettings(settings) {
        this.resetLastAccessTime();
        sessionStorage.setItem(this.USER_MAX_INACTIVE_SECONDS, settings.userMaxInactiveSeconds);
    }

    resetLastAccessTime() {
        sessionStorage.setItem(this.ACCESS_TOKEN_API_LAST_ACCESSED, Date.now());
    }

    async handleGetAccessTokenAsync(fetchParams, errorCallback = (error) => {
        console.error('Error in handleGetAccessTokenAsync', error);
    }) {
        const cfg = {
            auth: {
                authority: fetchParams.authority,
                clientId: fetchParams.clientId,
                redirectUri: fetchParams.redirectUri,
                knownAuthorities: [fetchParams.authority]
            },
        };

        const openid_conf = sessionStorage.getItem(this.OPENID_CONFIGURATION);
        if (openid_conf && openid_conf.length > 0) {
            cfg.auth.authorityMetadata = openid_conf;
        }

        const pca = new PublicClientApplication(cfg);
        const accounts = pca.getAllAccounts();
        const account = accounts[0];
        if (account) {
            pca.acquireTokenSilent({
                scopes: fetchParams.scopes,
                account,
            }).then((resp) => {
                sessionStorage.setItem(this.ACCESS_TOKEN, resp.accessToken);
            }).catch((err) => {
                errorCallback(err);
            });
        }
    }

    userIsActive() {
        const lastAccessedStr = sessionStorage.getItem(this.ACCESS_TOKEN_API_LAST_ACCESSED);
        if (lastAccessedStr && lastAccessedStr.length > 0) {
            const lastAccessed = Number(lastAccessedStr);
            const userMaxInactiveSecondsStr = sessionStorage.getItem(this.USER_MAX_INACTIVE_SECONDS);
            let userMaxInactiveSeconds = this.USER_DEFAULT_MAX_INACTIVE_SECONDS;
            if (userMaxInactiveSecondsStr && userMaxInactiveSecondsStr.length > 0) {
                userMaxInactiveSeconds = Number(userMaxInactiveSecondsStr);
            }
            if (Date.now() - lastAccessed > 1000 * userMaxInactiveSeconds) {
                return false;
            }
        }

        return true;
    }

    async acquireTokenAsync(errorCallback = (error) => {
        console.error('Error in acquireTokenAsync', error);
    }, userIdleCallback = () => {
        // do nothing
    }, apiAccess = true) {
        if (!this.userIsActive()) {
            userIdleCallback();
            return;
        }
        if (apiAccess) {
            this.resetLastAccessTime();
        }
        const fetchParamsString = sessionStorage.getItem(this.ACCESS_TOKEN_FETCH_PARAMS);
        if (fetchParamsString && fetchParamsString.length > 0) {
            const fetchParams = JSON.parse(fetchParamsString);
            this.handleGetAccessTokenAsync(fetchParams);
        }
        else {
            configurationService.get().subscribe((appCfg) => {
                const fetchParams = {
                    authority: appCfg.authority,
                    clientId: appCfg.clientId,
                    redirectUri: appCfg.redirectUri,
                    scopes: appCfg.scopes,
                    tenantName: appCfg.tenantName
                };
                sessionStorage.setItem(this.ACCESS_TOKEN_FETCH_PARAMS, JSON.stringify(fetchParams));

                const url = appCfg.authority + '/.well-known/openid-configuration';
                axios.get(url)
                    .then((response) => {
                        sessionStorage.setItem(this.OPENID_CONFIGURATION, JSON.stringify(response.data));
                    });
                this.handleGetAccessTokenAsync(fetchParams, errorCallback);
            });
        }
    }
}

export default new MSALUtil();
